import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import { Label } from "../../Components/UI/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../Components/UI/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../Components/UI/select";
import { MultiSelect } from "../../Components/UI/multi-select";
import { Textarea } from "../../Components/UI/textarea";
import { Checkbox } from "../../Components/UI/checkbox";
import MobileInput from "../../Components/Common/MobileInput";
import { X, Check, Loader2, ExternalLink } from "lucide-react";
import { BsCheck2All } from "react-icons/bs";
import { toast } from "sonner";
import {
  WSP_TEMPLATES,
  getStoredParamValue,
  setStoredParamValue,
} from "./WspTemplateConfigs";
import { sendWspTemplateAPI } from "./ReportAPI";
import { openRecordEntryModal } from "../../Utils/RecordEntryModal";

export default function UsageMISWspModal({ isOpen, onClose, agency }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState("app_version");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [paramValues, setParamValues] = useState({});
  const [isSending, setIsSending] = useState(false);

  const currentTemplate = useMemo(() => {
    return (
      WSP_TEMPLATES.find((t) => t.id === selectedTemplateId) ||
      WSP_TEMPLATES[0]
    );
  }, [selectedTemplateId]);

  // Initialize or re-populate fields when modal opens or agency changes
  useEffect(() => {
    if (!isOpen || !agency) return;

    // Clean and set mobile number
    const rawMobile =
      agency.mobile ||
      agency.Mobile ||
      agency.phone ||
      agency.MNo ||
      "";
    const cleaned = String(rawMobile).replace(/\D/g, "");
    setMobileNumber(cleaned.length === 12 && cleaned.startsWith("91") ? cleaned.slice(2) : cleaned);
    setCountryCode(agency.cc || "91");

    // Populate parameters
    initParameters(currentTemplate, agency);
  }, [isOpen, agency, currentTemplate]);

  const initParameters = (template, agn) => {
    if (!template || !template.params) return;
    const initialParams = {};

    template.params.forEach((p) => {
      if (p.persist) {
        // Load persisted value from cookie/storage
        initialParams[p.key] = getStoredParamValue(
          template.name,
          p.key,
          p.defaultValue || ""
        );
      } else if (p.defaultValueFromAgency) {
        // Automatically default from current agency row
        initialParams[p.key] = p.defaultValueFromAgency(agn);
      } else {
        initialParams[p.key] = p.defaultValue || "";
      }
    });

    setParamValues(initialParams);
  };

  const handleParamChange = (paramKey, value, shouldPersist) => {
    setParamValues((prev) => ({
      ...prev,
      [paramKey]: value,
    }));

    if (shouldPersist && currentTemplate) {
      setStoredParamValue(currentTemplate.name, paramKey, value);
    }
  };

  const handleTemplateChange = (newTmplId) => {
    setSelectedTemplateId(newTmplId);
    const tmpl = WSP_TEMPLATES.find((t) => t.id === newTmplId);
    if (tmpl) {
      initParameters(tmpl, agency);
    }
  };

  // Generate live interpolated message preview with filled data
  const interpolatedPreview = useMemo(() => {
    if (!currentTemplate) return "";
    let text = currentTemplate.bodyTemplate || "";

    currentTemplate.params.forEach((p, index) => {
      const placeholder = `{{${index + 1}}}`;
      const val =
        paramValues[p.key] !== undefined && paramValues[p.key] !== ""
          ? paramValues[p.key]
          : `[${p.label}]`;
      text = text.split(placeholder).join(val);
    });

    return text;
  }, [currentTemplate, paramValues]);

  const handleSend = async () => {
    if (!mobileNumber || mobileNumber.length < 7) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    if (!currentTemplate) {
      toast.error("Please select a template.");
      return;
    }

    // Validate params
    for (let i = 0; i < currentTemplate.params.length; i++) {
      const p = currentTemplate.params[i];
      if (!paramValues[p.key] || !paramValues[p.key].trim()) {
        toast.error(`Please provide value for ${p.label}`);
        return;
      }
    }

    setIsSending(true);

    try {
      const bodyParamsList = currentTemplate.params.map(
        (p) => paramValues[p.key]?.trim() || ""
      );
      const headerParamsList = currentTemplate.dynamicHeaderParams || [];
      const btnParamsList = currentTemplate.dynamicBtnParams || [];

      const payload = {
        mobile: mobileNumber.trim(),
        cc: countryCode.trim() || "91",
        tmplName: currentTemplate.name,
        headerParams: JSON.stringify(headerParamsList),
        bodyParams: JSON.stringify(bodyParamsList),
        btnParams: JSON.stringify(btnParamsList),
        agencyName: agency?.agencyName || agency?.AgencyName || "",
        agencyID: String(agency?.agencyID || agency?.AgencyID || ""),
      };

      const result = await sendWspTemplateAPI(payload);

      if (result && result.error) {
        toast.error(result.message || "Failed to send WhatsApp template message.");
        return;
      }

      toast.success(
        `WhatsApp message sent successfully to ${mobileNumber}!`
      );
      onClose();

      // Automatically open the interaction entry log so the user can save the record
      const agnName = agency?.agencyName || agency?.AgencyName || agency?.BizName || agency?.bizName || "Business";
      const contactName = agency?.contactName || agency?.ContactName || "";
      const templateTitle = currentTemplate.title || currentTemplate.name;
      const intNotes = `Sent official WhatsApp : ${templateTitle}`;

      setTimeout(() => {
        openRecordEntryModal({
          connID: agency?.connID || null,
          bizName: agnName,
          contactName: contactName,
          agencyName: agnName,
          recordType: "Message",
          notes: intNotes,
          connectionLocked: !!agency?.connID,
        });
      }, 300);
    } catch (error) {
      console.error("Error sending template message:", error);
      toast.error(error.message || "An error occurred while sending the message.");
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen || !agency) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-[100vw] sm:max-w-2xl lg:!max-w-[42vw] p-0 overflow-hidden bg-[#F1F1F0] sm:rounded-lg sahi-radius gap-0 shadow-xl border border-gray-200"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-[#F1F1F0]">
          <div className="text-lg md:text-xl font-semibold flex items-center gap-2 text-text-color">
            <span>Send WhatsApp Message</span>
            {agency?.agencyName && (
              <span className="text-sm font-normal text-muted-foreground truncate max-w-[240px]">
                - {agency.agencyName}
              </span>
            )}
          </div>
          <DialogClose asChild>
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none p-1 text-gray-500 hover:text-gray-900"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </DialogClose>
        </div>

        {/* Modal Body */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-4 max-h-[72vh] overflow-y-auto custom-scroll bg-[#F1F1F0]">
          {/* Form Section (7 cols) */}
          <div className="md:col-span-7 space-y-3.5">
            {/* Recipient Mobile with standard MobileInput */}
            <MobileInput
              label="Recipient Mobile Number"
              required={true}
              value={mobileNumber}
              onChange={(e) => {
                const val = typeof e === "string" ? e : (e?.target?.value !== undefined ? e.target.value : String(e || ""));
                setMobileNumber(val.replace(/\D/g, ""));
              }}
              selectedCountryCode={countryCode}
              onCountryCodeChange={(cc) => setCountryCode(cc)}
              placeholder="10-digit mobile number"
            />

            {/* Template Selector with UI Select */}
            <div className="space-y-1">
              <Label htmlFor="wspTemplate" className="text-sm font-medium text-text-color" required={true}>
                Template
              </Label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger id="wspTemplate" className="h-10 text-sm sahi-input bg-white">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {WSP_TEMPLATES.map((tmpl) => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Template Parameters */}
            {currentTemplate.params.map((param) => (
              <div key={param.key} className="space-y-1">
                <div className="flex justify-between items-center">
                  <Label htmlFor={param.key} className="text-sm font-medium text-text-color" required={true}>
                    {param.label}
                  </Label>
                  {param.persist && (
                    <span className="text-[11px] text-muted-foreground bg-gray-100 px-2 py-0.5 rounded">
                      Saved for next
                    </span>
                  )}
                </div>
                {param.key === "param1" ? (
                  <Textarea
                    id={param.key}
                    rows={3}
                    value={paramValues[param.key] || ""}
                    onChange={(e) =>
                      handleParamChange(param.key, e.target.value, param.persist)
                    }
                    placeholder={param.placeholder}
                    className="sahi-input text-sm resize-none"
                  />
                ) : (
                  <Input
                    id={param.key}
                    value={paramValues[param.key] || ""}
                    onChange={(e) =>
                      handleParamChange(param.key, e.target.value, param.persist)
                    }
                    placeholder={param.placeholder}
                    className="sahi-input h-10 text-sm"
                  />
                )}
              </div>
            ))}
          </div>

          {/* WhatsApp Chat Preview Section (5 cols) */}
          <div className="md:col-span-5 flex flex-col space-y-2">
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-text-color">Message Preview</h3>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1 truncate">
                To: {agency?.contactName ? `${agency.contactName} (${agency.agencyName || "Agency"})` : (agency?.agencyName || "Customer")}
              </p>
            </div>

            {/* WhatsApp Chat Styled Container */}
            <div className="rounded-xl p-3 sm:p-4 bg-[#EFEAE2] border border-[#D1C7BC] flex-1 flex flex-col justify-between min-h-[260px] shadow-xs relative">
              {/* WhatsApp Message Bubble */}
              <div className="bg-white rounded-lg rounded-tl-none p-3 shadow-xs space-y-2 text-left border border-black/5 relative">
                {/* Header if template has one */}
                {currentTemplate.header && (
                  <div className="font-bold text-xs sm:text-sm text-slate-900 pb-1 border-b border-slate-100">
                    {currentTemplate.header}
                  </div>
                )}

                {/* Body Text */}
                <div className="text-xs sm:text-xs text-slate-800 leading-relaxed whitespace-pre-line">
                  {interpolatedPreview}
                </div>

                {/* Message Time and Blue Double Ticks */}
                <div className="flex items-center justify-end gap-1 text-[10px] text-slate-400 pt-0.5">
                  <span>Just now</span>
                  <BsCheck2All className="w-3.5 h-3.5 text-[#53bdeb]" />
                </div>

                {/* Interactive Button */}
                {currentTemplate.button && (
                  <div className="pt-2 border-t border-slate-100">
                    <div className="w-full py-1.5 px-3 rounded-md bg-slate-50 border border-slate-200/80 text-[#00a884] font-semibold text-xs flex items-center justify-center gap-1.5 shadow-2xs">
                      <ExternalLink className="w-3 h-3" />
                      <span>{currentTemplate.button.label}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Notice text */}
              <div className="text-[10px] text-slate-500 text-center mt-2">
                Official WhatsApp Business Template
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-start gap-3 bg-[#F1F1F0]">
          <Button
            className="sahi-primary-button gap-2"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" strokeWidth={3} />
                Send Message
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className="sahi-secondary-button"
            disabled={isSending}
          >
            <X className="w-3 h-3" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
